import { create } from 'zustand';
import { generateSpreadOptions, getMaxSpreadsForPaperType } from '../lib/utils';
import { CLAMSHELL_TRAY_COVER_FABRIC_NAMES, COMING_SOON_ALBUM_TYPES, MUSEUM_CASE_ORDER_HERE_URL, DOUBLE_FOLIO_ORDER_HERE_URL } from '../lib/const';
import httpClient from '../httpClient';

const fabricTextIsClamshellTrayCoverFabricName = (text) => {
  if (!text) return false;
  const normalized = text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim().toLowerCase();
  return CLAMSHELL_TRAY_COVER_FABRIC_NAMES.some(
    (name) => name.trim().toLowerCase() === normalized,
  );
};

const buildClamshellUpgradeSelectOptions = (clamshellPriceParsed) => {
  const yesText =
    clamshellPriceParsed != null && Number.isFinite(clamshellPriceParsed) && clamshellPriceParsed > 0
      ? `Yes ($${clamshellPriceParsed.toFixed(2)})`
      : 'Yes';
  return [
    { value: 'yes', text: yesText },
    { value: 'no', text: 'No' },
  ];
};

const albumSizeApiRowToSelectOption = (size) => {
  const sizeName = size.attributes?.albumSizeName;
  const price = parseFloat(size.attributes?.price) || 0;
  const clamshellPriceRaw = size.attributes?.clamshellPrice || 0;
  let clamshellPrice = null;
  if (clamshellPriceRaw !== null && clamshellPriceRaw !== undefined && clamshellPriceRaw !== '') {
    clamshellPrice = parseFloat(clamshellPriceRaw);
  }

  return {
    value: size.attributes?.guid,
    text: `${sizeName}`,
    price,
    perSpreadPrice:
      parseFloat(size.attributes?.perSpreadPrice || 0),
    baseSpreads: size.attributes?.baseSpreads || 10,
    clamshellPrice,
  };
};

const albumFieldIsRequired = (input, requiredInputs) => {
  if (!input) return false;
  if (input.requiredInput) {
    return requiredInputs.includes(input.requiredInput);
  }
  const inputLabel = input.label;
  return requiredInputs.some((req) => inputLabel?.toLowerCase().includes(req.toLowerCase()));
};

const optionByValue = (options, value) => {
  if (value === undefined || value === null) return undefined;
  return options.find((option) => String(option.value) === String(value));
};

const optionByText = (options, value) => {
  console.log("Option By tex", options, "Values", value)
  if (!value) return undefined;
  const normalizedValue = String(value).trim().toLowerCase();
  return options.find((option) => {
    const normalizedText = String(option.text || '').trim().toLowerCase();
    return normalizedText === normalizedValue
      || normalizedValue.startsWith(normalizedText)
      || normalizedText.startsWith(normalizedValue);
  });
};

const firstDefined = (...values) => values.find(
  (value) => value !== undefined && value !== null && value !== '',
);

const scalarValue = (...values) => firstDefined(
  ...values.filter((value) => typeof value !== 'object'),
);

const mapAlbumEditPhotos = (attributes = {}) => {
  const photos = attributes.albumPhotos || attributes.album_photos || [];
  return photos.map((photo) => ({
    id: photo.photoGuid || photo.photo_guid,
    type: 'lab_order_photograph',
    attributes: {
      guid: photo.photoGuid || photo.photo_guid,
      url: photo.photoUrl || photo.photo_url,
      presignedPhotoUrl: photo.photoUrl || photo.photo_url,
      smallestPhoto: photo.photoUrl || photo.photo_url,
      fileName: photo.photoName || photo.photo_name,
      photoFileName: photo.photoName || photo.photo_name,
      productType: 'albums',
      isAddedToCart: false,
    },
  }));
};

const useAlbumSelectionStore = create((set, get) => ({
  updateAlbumLineItem: async (apiLabOrderGuid, apiAlbumOrderDetailGuid, body) =>
    httpClient.patch(
      `lab_orders/${apiLabOrderGuid}/albums/${apiAlbumOrderDetailGuid}`,
      body,
    ),

  albumQueryStates: {
    albumTypes: { isLoading: false, isError: false, isSuccess: false },
    albumPapers: { isLoading: false, isError: false, isSuccess: false },
    albumSizes: { isLoading: false, isError: false, isSuccess: false },
    fabrics: { isLoading: false, isError: false, isSuccess: false },
    stampings: { isLoading: false, isError: false, isSuccess: false },
    fontOptions: { isLoading: false, isError: false, isSuccess: false },
    preDesignedCovers: { isLoading: false, isError: false, isSuccess: false },
  },

  albumEditLineItem: null,
  setAlbumEditLineItem: (albumEditLineItem) => set({ albumEditLineItem }),

  isAlbumEditInitializing: false,
  setIsAlbumEditInitializing: (isAlbumEditInitializing) => set({ isAlbumEditInitializing }),

  hydrateFromLineItem: (attributes) => {
    const photos = mapAlbumEditPhotos(attributes);
    const totalPrice = parseFloat(attributes?.totalPrice ?? attributes?.total_price);

    set({
      albumEditLineItem: {
        type: 'album_order_detail',
        attributes,
      },
      isAlbumEditInitializing: true,
      albumItems: photos,
    });

    const { albumTypesData, processAlbumTypesData } = get();
    if (albumTypesData) {
      processAlbumTypesData(albumTypesData);
    }

    return {
      photos,
      subtotal: Number.isNaN(totalPrice) ? '0.00' : totalPrice.toFixed(2),
      description: attributes?.description || '',
      notes: attributes?.notes || '',
    };
  },

  albumItems: [],
  setAlbumItems: (albumItems) => {
    const fileCount = albumItems.length;
    const spreads = fileCount > 0 ? Math.max(10, fileCount) : undefined;

    set((state) => {
      const nextState = { albumItems };
      nextState.defaultAlbumOptions = {
        ...state.defaultAlbumOptions,
        numberOfSpreads: {
          ...state.defaultAlbumOptions.numberOfSpreads,
          selectedValue: spreads ? spreads.toString() : undefined,
          selectedValueText: spreads ? `${spreads} Spreads` : undefined,
        },
      };
      return nextState;
    });

    const { setAlbumForm } = get();
    if (fileCount > 0) {
      setAlbumForm((prevState) => {
        if (!prevState?.inputs?.numberOfSpreads) return prevState;
        if (prevState.inputs.numberOfSpreads.selectedValue === spreads.toString()) return prevState;
        return {
          ...prevState,
          inputs: {
            ...prevState.inputs,
            numberOfSpreads: {
              ...prevState.inputs.numberOfSpreads,
              selectedValue: spreads.toString(),
              selectedValueText: `${spreads} Spreads`,
            },
          },
        };
      });
    }
  },

  setAlbumQueryState: (queryName, state) => set((prev) => ({
    albumQueryStates: {
      ...prev.albumQueryStates,
      [queryName]: { ...prev.albumQueryStates[queryName], ...state },
    },
  })),

  albumTypesData: null,
  setAlbumTypesData: (data) => set({ albumTypesData: data }),

  albumPapersData: null,
  setAlbumPapersData: (data) => set({ albumPapersData: data }),

  albumSizesData: null,
  setAlbumSizesData: (data) => set({ albumSizesData: data }),

  fabricsData: null,
  setFabricsData: (data) => set({ fabricsData: data }),

  stampingsData: null,
  setStampingsData: (data) => set({ stampingsData: data }),

  fontOptionsData: null,
  setFontOptionsData: (data) => set({ fontOptionsData: data }),

  preDesignedCoversData: null,
  setPreDesignedCoversData: (data) => set({ preDesignedCoversData: data }),

  // Album photos state for drag-and-drop
  albumPhotos: [],
  setAlbumPhotos: (photos) => set({ albumPhotos: photos }),
  addPhotoToAlbum: (photo) => {
    const { albumPhotos } = get();
    // Prevent duplicate photos
    const photoId = photo?.attributes?.guid || photo?.id;
    const exists = albumPhotos.some(p => (p?.attributes?.guid || p?.id) === photoId);
    if (!exists) {
      set({ albumPhotos: [...albumPhotos, photo] });
    }
  }, removePhotoFromAlbum: (photoId) => set((state) => ({
    albumPhotos: state.albumPhotos.filter(p => (p?.attributes?.guid || p?.id) !== photoId)
  })),
  reorderAlbumPhotos: (oldIndex, newIndex) => {
    const { albumPhotos } = get();
    const newPhotos = [...albumPhotos];
    const [removed] = newPhotos.splice(oldIndex, 1);
    newPhotos.splice(newIndex, 0, removed);
    set({ albumPhotos: newPhotos });
  },
  resetAlbumPhotos: () => set({ albumPhotos: [] }),

  processFontOptionsData: (response) => {
    const {
      setFontOptionsData,
      setAlbumForm,
      albumEditLineItem,
      isAlbumEditInitializing,
    } = get();

    setFontOptionsData(response);

    const fontOptions = (response?.data && Array.isArray(response.data))
      ? response.data.map((font) => ({
        value: font.id || font.attributes?.guid,
        text: font.attributes?.fontType || font.attributes?.font_type,
      }))
      : [];

    const editAttrs = albumEditLineItem?.attributes;
    const shouldAutoMap = albumEditLineItem && isAlbumEditInitializing;
    const mappedFont = shouldAutoMap
      ? optionByValue(fontOptions, editAttrs?.fontOptionId)
        || optionByText(fontOptions, editAttrs?.font)
      : undefined;
    const mappedClamshellFont = shouldAutoMap
      ? optionByValue(fontOptions, editAttrs?.clamshell?.fontOptionId)
        || optionByText(fontOptions, editAttrs?.clamshell?.font)
      : undefined;

    if (shouldAutoMap && mappedFont && get().albumForm.inputs?.coverOptions) {
      get().updateAlbumInput('coverOptions', 'text-only', 'Text Only');
      get().updateAlbumInput('fontOption', mappedFont.value, mappedFont.text);
    }
    if (shouldAutoMap && mappedClamshellFont && get().albumForm.inputs?.clamshellCoverOptions) {
      get().updateAlbumInput('clamshellCoverOptions', 'text-only', 'Text Only');
      get().updateAlbumInput(
        'clamshellFontOption',
        mappedClamshellFont.value,
        mappedClamshellFont.text,
      );
    }

    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };

      if (newInputs.fontOption) {
        const selectedValue = newInputs.fontOption.selectedValue || mappedFont?.value;
        const selectedValueText = newInputs.fontOption.selectedValueText || mappedFont?.text;

        newInputs.fontOption = {
          ...newInputs.fontOption,
          options: fontOptions,
          selectedValue,
          selectedValueText,
          isLoading: false,
          isDisabled: false,
        };
      }

      if (newInputs.clamshellFontOption) {
        const selectedValue = newInputs.clamshellFontOption.selectedValue
          || mappedClamshellFont?.value;
        const selectedValueText = newInputs.clamshellFontOption.selectedValueText
          || mappedClamshellFont?.text;

        newInputs.clamshellFontOption = {
          ...newInputs.clamshellFontOption,
          options: fontOptions,
          selectedValue,
          selectedValueText,
          isLoading: false,
        };
      }

      return {
        ...prevState,
        inputs: newInputs,
      };
    });
  },

  setFontOptionsLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };

      // Update regular fontOption if it exists
      if (newInputs.fontOption) {
        newInputs.fontOption = {
          ...newInputs.fontOption,
          isLoading,
        };
      }

      // Update clamshell fontOption if it exists
      if (newInputs.clamshellFontOption) {
        newInputs.clamshellFontOption = {
          ...newInputs.clamshellFontOption,
          isLoading,
        };
      }

      return {
        ...prevState,
        inputs: newInputs,
      };
    });
  },

  preDesignedCoversData: null,
  setPreDesignedCoversData: (data) => set({ preDesignedCoversData: data }),

  // Album photos state for drag-and-drop
  albumPhotos: [],
  setAlbumPhotos: (photos) => set({ albumPhotos: photos }),
  addPhotoToAlbum: (photo) => {
    const { albumPhotos } = get();
    // Prevent duplicate photos
    const photoId = photo?.attributes?.guid || photo?.id;
    const exists = albumPhotos.some(p => (p?.attributes?.guid || p?.id) === photoId);
    if (!exists) {
      set({ albumPhotos: [...albumPhotos, photo] });
    }
  }, removePhotoFromAlbum: (photoId) => set((state) => ({
    albumPhotos: state.albumPhotos.filter(p => (p?.attributes?.guid || p?.id) !== photoId)
  })),
  reorderAlbumPhotos: (oldIndex, newIndex) => {
    const { albumPhotos } = get();
    const newPhotos = [...albumPhotos];
    const [removed] = newPhotos.splice(oldIndex, 1);
    newPhotos.splice(newIndex, 0, removed);
    set({ albumPhotos: newPhotos });
  },
  resetAlbumPhotos: () => set({ albumPhotos: [] }),

  defaultAlbumOptions: {
    meta: {
      subtotal: 0,
      requiredInputs: ['Album Type'],
      albumTypeId: undefined,
      albumPaperId: undefined,
      albumSizeId: undefined,
      outerFabricId: undefined,
      innerFabricId: undefined,
      stampingId: undefined,
      fontOptionId: undefined,
      preDesignedCoverId: undefined,
    },
    albumType: {
      label: 'Choose Album Type',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: true,
      isDisabled: false,
      style: {
        gridColumn: 'span 2',
      },
    },
    albumPaper: {
      label: 'Paper Type',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select an album type',
      style: {
        gridColumn: 'span 1',
      },
    },
    albumSize: {
      label: 'Album Size',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a paper type',
      style: {
        gridColumn: 'span 1',
      },
    },
    coverFabric: {
      label: 'Cover Fabric',
      tooltip: { text: 'To see pre-designed covers visit ', url: 'https://musealab.com/fabrics' },
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select an album size',
      style: {
        gridColumn: 'span 2',
      },
    },
    interiorFabric: {
      label: 'Interior Fabric',
      tooltip: { text: 'To see pre-designed covers visit ', url: 'https://musealab.com/fabrics' },
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a cover fabric',
      style: {
        gridColumn: 'span 2',
      },
    },
    numberOfSpreads: {
      label: 'Number of Spreads',
      inputType: 'select',
      options: generateSpreadOptions(),
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select fabrics first',
      style: {
        gridColumn: 'span 2',
      },
    },
    debossingType: {
      label: 'Debossing or Foil Stamping',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    coverOptions: {
      label: 'Cover Options',
      inputType: 'select',
      options: [
        { value: 'pre-designed', text: 'Pre-Designed Cover' },
        { value: 'text-only', text: 'Text Only' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      isHidden: true,
      disabledMessage: 'Please select a stamping type',
      style: {
        gridColumn: 'span 2',
      },
    },
    preDesignedCover: {
      label: 'Pre-Designed Cover',
      tooltip: { text: 'To see pre-designed covers visit', url: 'https://musealab.com/covers' },
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    fontOption: {
      label: 'Font For Cover ',
      tooltip: { text: 'To see pre-designed covers visit', url: 'https://musealab.com/covers' },
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    coverText: {
      label: 'Text for Cover',
      inputType: 'text',
      placeholder: 'Enter cover text',
      selectedValue: '',
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    debossingTextTop: {
      label: 'Debossing / Foil Stamping Text',
      labelHtml: 'Debossing / Foil Stamping Text | <span class="green">top line</span>',
      inputType: 'text',
      placeholder: 'Example Text',
      selectedValue: '',
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a stamping type',
      style: {
        gridColumn: 'span 2',
      },
    },
    debossingTextBottom: {
      label: '(Optional) Debossing / Foil Stamping Text',
      labelHtml: '<span class="grey">(Optional)</span> Debossing / Foil Stamping Text | <span class="green">Bottom line</span>',
      inputType: 'text',
      placeholder: 'Example Text',
      selectedValue: '',
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a stamping type',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellUpgrade: {
      label: 'Clamshell Upgrade',
      inputType: 'select',
      options: [
        { value: 'yes', text: 'Yes' },
        { value: 'no', text: 'No' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    // Additional options for specific album types
    numberOfImages: {
      label: 'Number of Images',
      inputType: 'select',
      options: Array.from({ length: 11 }, (_, i) => ({
        value: String((i + 5) * 2),
        text: String((i + 5) * 2),
      })),
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 1',
      },
    },
    numberOfMattedPrints: {
      label: 'Number of Matted Prints',
      inputType: 'select',
      options: Array.from({ length: 16 }, (_, i) => ({
        value: String(i + 10),
        text: String(i + 10),
      })),
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 2',
      },
    },
    trayFabric: {
      label: 'Select Tray Fabric',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 1',
      },
    },
    doubleFolioOrientation: {
      label: 'Double Folio Orientation',
      inputType: 'select',
      options: [
        { value: 'horizontal', text: 'Horizontal' },
        { value: 'vertical', text: 'Vertical' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: false,
      style: {
        gridColumn: 'span 1',
      },
    },
    slipcaseOption: {
      label: 'Slipcase Option',
      inputType: 'select',
      options: [
        { value: 'yes', text: 'Yes' },
        { value: 'no', text: 'No' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 1',
      },
    },
    slipcaseFabric: {
      label: 'Slipcase Fabric',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 1',
      },
    },
    fourFlapColorOption: {
      label: 'Four-Flap Color Option',
      inputType: 'select',
      options: [
        { value: 'sage', text: 'Sage' },
        { value: 'smoke', text: 'Smoke' },
        { value: 'walnut', text: 'Walnut' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      style: {
        gridColumn: 'span 1',
      },
    },

    clamshellOutsideFabric: {
      label: 'Clamshell Outside Cover Fabric',
      inputType: 'select',
      requiredInput: 'Clamshell Outside Cover Fabric',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select clamshell upgrade first',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellTrayFabric: {
      label: 'Clamshell Tray Cover Fabric',
      inputType: 'select',
      requiredInput: 'Clamshell Tray Cover Fabric',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select clamshell outside fabric first',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellDebossingType: {
      label: 'Debossing or Foil Stamping',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellCoverOptions: {
      label: 'Cover Options',
      inputType: 'select',
      options: [
        { value: 'pre-designed', text: 'Pre-Designed Cover' },
        { value: 'text-only', text: 'Text Only' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      isHidden: true,
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellPreDesignedCover: {
      label: 'Pre-Designed Cover',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      isHidden: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellFontOption: {
      label: 'Font for Cover',
      inputType: 'select',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: false,
      isDisabled: true,
      isHidden: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
    clamshellCoverText: {
      label: 'Text for Cover',
      inputType: 'text',
      placeholder: 'Enter cover text',
      selectedValue: '',
      isLoading: false,
      isDisabled: true,
      isHidden: true,
      disabledMessage: 'Please complete previous selections',
      style: {
        gridColumn: 'span 2',
      },
    },
  },

  albumForm: {
    meta: {
      subtotal: 0,
      requiredInputs: ['Album Type'],
      albumTypeId: undefined,
      albumPaperId: undefined,
      albumSizeId: undefined,
      outerFabricId: undefined,
      innerFabricId: undefined,
      stampingId: undefined,
    },
    inputs: {},
  },
  setAlbumForm: (formOrUpdater) => {
    if (typeof formOrUpdater === 'function') {
      set((state) => ({ albumForm: formOrUpdater(state.albumForm) }));
    } else {
      set({ albumForm: formOrUpdater });
    }
  },

  getAlbumForm: () => {
    const { defaultAlbumOptions, albumTypesData } = get();

    let albumTypeInput = { ...defaultAlbumOptions.albumType };
    if (albumTypesData?.data) {
      const albumTypeOptions = albumTypesData.data.map((albumType) => ({
        value: albumType.id || albumType.attributes?.guid,
        text: albumType.attributes?.albumName || albumType.attributes?.album_name,
      }));

      // add museum case and double folio as coming-soon options
      albumTypeOptions.push(
        { value: 'museum-case', text: 'Museum Case', comingSoon: true, orderUrl: MUSEUM_CASE_ORDER_HERE_URL },
        { value: 'double-folio', text: 'Double Folio', comingSoon: true, orderUrl: DOUBLE_FOLIO_ORDER_HERE_URL }
      );
      albumTypeInput = {
        ...defaultAlbumOptions.albumType,
        options: albumTypeOptions,
        isLoading: false,
        isDisabled: false,
      };
    }

    return {
      meta: {
        subtotal: 0,
        requiredInputs: ['Album Type'],
        albumTypeId: undefined,
        albumPaperId: undefined,
        albumSizeId: undefined,
      },
      inputs: {
        albumType: albumTypeInput,
      },
    };
  },


  processAlbumTypesData: (response) => {
    const { setAlbumTypesData, setAlbumForm, defaultAlbumOptions,  albumEditLineItem, isAlbumEditInitializing } = get();

    setAlbumTypesData(response);

    const albumTypeOptions = response.data.map((albumType) => ({
      value: albumType.id || albumType.attributes?.guid,
      text: albumType.attributes?.albumName || albumType.attributes?.album_name,
    }));

    albumTypeOptions.push(
      { value: 'museum-case', text: 'Museum Case', comingSoon: true, orderUrl: MUSEUM_CASE_ORDER_HERE_URL },
      {
        value: 'double-folio',
        text: 'Double Folio',
        comingSoon: true,
        orderUrl: DOUBLE_FOLIO_ORDER_HERE_URL,
      },
    );

    const editAttrs = albumEditLineItem?.attributes;
    const matchedAlbumType = albumEditLineItem && isAlbumEditInitializing
      ? optionByValue(
        albumTypeOptions,
        firstDefined(
          editAttrs?.albumTypeId,
          editAttrs?.albumTypeGuid,
          editAttrs?.album_type_id,
          editAttrs?.album_type_guid,
          editAttrs?.albumType?.id,
          editAttrs?.albumType?.guid,
          editAttrs?.albumType?.attributes?.guid,
        ),
      ) || optionByText(
        albumTypeOptions,
        scalarValue(
          editAttrs?.albumTypeName,
          editAttrs?.album_type_name,
          editAttrs?.albumName,
          editAttrs?.albumType,
          editAttrs?.description,
          editAttrs?.albumType?.name,
          editAttrs?.albumType?.albumName,
          editAttrs?.albumType?.attributes?.albumName,
        ),
      )
      : undefined;

    setAlbumForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        albumType: {
          ...defaultAlbumOptions.albumType,
          options: albumTypeOptions,
          selectedValue: matchedAlbumType?.value ?? prevState.inputs?.albumType?.selectedValue,
          selectedValueText: matchedAlbumType?.text
            ?? prevState.inputs?.albumType?.selectedValueText,
          isLoading: false,
          isDisabled: false,
        },
      },
    }));
  },
  processAlbumPapersData: (response) => {
    const { setAlbumPapersData, setAlbumForm, defaultAlbumOptions, albumEditLineItem, isAlbumEditInitializing, } = get();

    setAlbumPapersData(response);

    const albumPaperOptions = response.data.map((paper) => ({
      value: paper.id || paper.attributes?.guid,
      text: paper.attributes?.albumPaperName || paper.attributes?.album_paper_name,
    }));

    const editAttrs = albumEditLineItem?.attributes;
    
    const matchedAlbumPaper = albumEditLineItem && isAlbumEditInitializing
      ?  optionByText(albumPaperOptions,
        scalarValue(
          editAttrs?.albumPaperName,
        ),
      )
      : undefined;

    setAlbumForm((prevState) => {
      const currentSelection = prevState.inputs.albumPaper?.selectedValue || matchedAlbumPaper?.value;
      const isSelectionValid = currentSelection && albumPaperOptions.some((option) => option.value === currentSelection);
      return {
        ...prevState,
        inputs: {
          ...prevState.inputs,
          albumPaper: {
            ...defaultAlbumOptions.albumPaper,
            options: albumPaperOptions,
            selectedValue: isSelectionValid ? currentSelection : undefined,
            selectedValueText: isSelectionValid ? (prevState.inputs.albumPaper?.selectedValueText || matchedAlbumPaper?.text) : undefined,
            isLoading: false,
            isDisabled: false,
          },
        },
      };
    });
  },

  processAlbumSizesData: (response) => {
    const { setAlbumSizesData, setAlbumForm, defaultAlbumOptions, albumEditLineItem, isAlbumEditInitializing } = get();

    setAlbumSizesData(response);

    const albumSizeOptions = response.data.map(albumSizeApiRowToSelectOption);

    const editAttrs = albumEditLineItem?.attributes;
    const editSize = albumEditLineItem && isAlbumEditInitializing
      ? optionByValue(
        albumSizeOptions,
        firstDefined(
          editAttrs?.albumSizePriceId,
        ),
      )
      : undefined;

    setAlbumForm((prevState) => {
      const currentValue = prevState.inputs?.albumSize?.selectedValue;
      const selectedSize = optionByValue(albumSizeOptions, currentValue) || editSize;
      return {
        ...prevState,
        meta: {
          ...prevState.meta,
          albumSizeId: selectedSize?.value,
          basePrice: selectedSize?.price,
          baseSpreads: selectedSize?.baseSpreads || prevState.meta?.baseSpreads,
          perSpreadPrice: selectedSize?.perSpreadPrice || prevState.meta?.perSpreadPrice,
        },
        inputs: {
          ...prevState.inputs,
          albumSize: {
            ...defaultAlbumOptions.albumSize,
            options: albumSizeOptions,
            selectedValue: selectedSize?.value,
            selectedValueText: selectedSize?.text,
            selectedValuePrice: selectedSize?.price,
            isLoading: false,
            isDisabled: false,
          },
          ...(prevState.inputs?.clamshellUpgrade && {
            clamshellUpgrade: {
              ...prevState.inputs.clamshellUpgrade,
              options: buildClamshellUpgradeSelectOptions(selectedSize?.clamshellPrice),
            },
          }),
        },
      };
    });
  },

  processFabricsData: (response) => {
    const { setFabricsData, setAlbumForm,albumEditLineItem, isAlbumEditInitializing } = get();

    setFabricsData(response);

    const fabricOptions = response.data.map((fabric) => ({
      value: fabric.id || fabric.attributes?.guid,
      text: fabric.attributes?.fabricName || fabric.attributes?.fabric_name,
    }));

    fabricOptions.sort((a, b) => a.text.localeCompare(b.text));

    const editAttrs = albumEditLineItem?.attributes;
    console.log("Album Attributes", editAttrs )
    const shouldAutoMap = albumEditLineItem && isAlbumEditInitializing;
    const mappedFabrics = shouldAutoMap ? {
      coverFabric: optionByValue(
        fabricOptions,
        firstDefined(editAttrs?.outerFabricId),
      ), 
      interiorFabric: optionByValue(
        fabricOptions,
        firstDefined(editAttrs?.innerFabricId),
      ),
      trayFabric: optionByValue(
        fabricOptions,
        firstDefined(editAttrs?.trayFabricId, editAttrs?.tray_fabric_id),
      ) || optionByText(
        fabricOptions,
        scalarValue(editAttrs?.trayFabricName, editAttrs?.tray_fabric_name),
      ),
      slipcaseFabric: optionByValue(
        fabricOptions,
        firstDefined(editAttrs?.slipcaseFabricId, editAttrs?.slipcase_fabric_id),
      ) || optionByText(
        fabricOptions,
        scalarValue(editAttrs?.slipcaseFabricName, editAttrs?.slipcase_fabric_name),
      ),
      clamshellOutsideFabric: optionByText(
        fabricOptions,
        scalarValue(
          editAttrs?.clamshell?.outerFabricName,
          editAttrs?.clamshell?.outer_fabric_name,
        ),
      ),
      clamshellTrayFabric: optionByText(
        fabricOptions,
        scalarValue(
          editAttrs?.clamshell?.trayFabricName,
          editAttrs?.clamshell?.tray_fabric_name,
        ),
      ),
    } : {};

    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };

      // Update cover fabric options - keep existing selection if valid
      if (newInputs.coverFabric) {
        const mappedOption = mappedFabrics.coverFabric;
        const currentSelection = newInputs.coverFabric.selectedValue || mappedOption?.value;
        const isSelectionValid = currentSelection && fabricOptions.some(opt => opt.value === currentSelection);
        
        newInputs.coverFabric = {
          ...newInputs.coverFabric,
          options: fabricOptions,
          selectedValue: isSelectionValid ? currentSelection : undefined,
          selectedValueText: isSelectionValid
            ? (newInputs.coverFabric.selectedValueText || mappedOption?.text)
            : undefined,
          isLoading: false,
          isDisabled: false,
        };
      }

      if (newInputs.interiorFabric) {
        const mappedOption = mappedFabrics.interiorFabric;
        const currentSelection = newInputs.interiorFabric.selectedValue || mappedOption?.value;
        const isSelectionValid = currentSelection && fabricOptions.some(opt => opt.value === currentSelection);
        
        newInputs.interiorFabric = {
          ...newInputs.interiorFabric,
          options: fabricOptions,
          selectedValue: isSelectionValid ? currentSelection : undefined,
          selectedValueText: isSelectionValid
            ? (newInputs.interiorFabric.selectedValueText || mappedOption?.text)
            : undefined,
          isLoading: false,
          isDisabled: !newInputs.coverFabric?.selectedValue,
        };
      }

      if (newInputs.trayFabric) {
        const mappedOption = mappedFabrics.trayFabric;
        const currentSelection = newInputs.trayFabric.selectedValue || mappedOption?.value;
        const isSelectionValid = currentSelection && fabricOptions.some(opt => opt.value === currentSelection);
        
        newInputs.trayFabric = {
          ...newInputs.trayFabric,
          options: fabricOptions,
          selectedValue: isSelectionValid ? currentSelection : undefined,
          selectedValueText: isSelectionValid
            ? (newInputs.trayFabric.selectedValueText || mappedOption?.text)
            : undefined,
          isLoading: false,
          isDisabled: false,
        };
      }

      // if (newInputs.slipcaseFabric) {
      //   const mappedOption = mappedFabrics.slipcaseFabric;
      //   const currentSelection = newInputs.slipcaseFabric.selectedValue || mappedOption?.value;
      //   const isSelectionValid = currentSelection && fabricOptions.some(opt => opt.value === currentSelection);
        
      //   newInputs.slipcaseFabric = {
      //     ...newInputs.slipcaseFabric,
      //     options: fabricOptions,
      //     selectedValue: isSelectionValid ? currentSelection : undefined,
      //     selectedValueText: isSelectionValid
      //       ? (newInputs.slipcaseFabric.selectedValueText || mappedOption?.text)
      //       : undefined,
      //     isLoading: false,
      //     isDisabled: false,
      //   };
      // }

      ['clamshellOutsideFabric', 'clamshellTrayFabric'].forEach((key) => {
        if (!newInputs[key]) return;
        const mappedOption = mappedFabrics[key];
        const currentSelection = newInputs[key].selectedValue || mappedOption?.value;
        const isSelectionValid = currentSelection
          && fabricOptions.some((option) => String(option.value) === String(currentSelection));
        newInputs[key] = {
          ...newInputs[key],
          options: key === 'clamshellTrayFabric'
            ? fabricOptions.filter(
              (option) => !fabricTextIsClamshellTrayCoverFabricName(option.text),
            )
            : fabricOptions,
          selectedValue: isSelectionValid ? currentSelection : undefined,
          selectedValueText: isSelectionValid
            ? (newInputs[key].selectedValueText || mappedOption?.text)
            : undefined,
          isLoading: false,
          isDisabled: key === 'clamshellTrayFabric'
            ? !newInputs.clamshellOutsideFabric?.selectedValue
            : false,
        };
      });

      return {
        ...prevState,
        inputs: newInputs,
      };
    });
  },

  processStampingsData: (response) => {
    const {
      setStampingsData,
      setAlbumForm,
      defaultAlbumOptions,
      albumEditLineItem,
      isAlbumEditInitializing,
    } = get();

    setStampingsData(response);

    const stampingOptions = response.data.map((stamping) => {
      const stampingType = stamping.attributes?.stampingType
        || stamping.attributes?.stamping_type;
      const price = parseFloat(stamping.attributes?.price) || 0;

      const displayText = price > 0
        ? `${stampingType} ($${price.toFixed(2)})`
        : stampingType;
      return {
        value: stamping.id || stamping.attributes?.guid,
        text: displayText,
        price,
      };
    });

    stampingOptions.unshift({ value: 'none', text: 'None', price: 0, stampingType: 'None' });

    const editAttrs = albumEditLineItem?.attributes;
    const shouldAutoMap = albumEditLineItem && isAlbumEditInitializing;
    const matchedStamping = shouldAutoMap
      ? optionByValue(stampingOptions, editAttrs?.stampingId)
        || optionByText(stampingOptions, editAttrs?.stamping)
        || (!editAttrs?.stampingId && !editAttrs?.stamping
          ? optionByValue(stampingOptions, 'none')
          : undefined)
      : undefined;

    if (shouldAutoMap) {
      const setSelect = (key, value, text) => {
        if (get().albumForm.inputs?.[key] && value !== undefined && value !== null) {
          get().updateAlbumInput(key, String(value), text || String(value));
        }
      };

      const photoCount = get().albumItems.length;
      const spreadQuantity = photoCount > 0
        ? Math.max(10, photoCount)
        : editAttrs?.spreadQuantity ?? editAttrs?.spread_quantity;

      setSelect('numberOfSpreads', spreadQuantity, `${spreadQuantity} Spreads`);
      const numberOfImages = editAttrs?.numberOfImages ?? editAttrs?.number_of_images;
      setSelect('numberOfImages', numberOfImages, String(numberOfImages));
      setSelect(
        'numberOfMattedPrints',
        editAttrs?.mattedPrintQuantity ?? editAttrs?.matted_print_quantity,
        String(editAttrs?.mattedPrintQuantity ?? editAttrs?.matted_print_quantity),
      );

      if (matchedStamping) {
        get().updateAlbumInput(
          'debossingType',
          matchedStamping.value,
          matchedStamping.text,
          { price: matchedStamping.price },
        );
      }

      const hasClamshell = Boolean(editAttrs?.clamshell);
      if (get().albumForm.inputs?.clamshellUpgrade) {
        const sizeValue = get().albumForm.inputs?.albumSize?.selectedValue;
        const sizeOption = optionByValue(
          get().albumForm.inputs?.albumSize?.options || [],
          sizeValue,
        );
        get().updateAlbumInput(
          'clamshellUpgrade',
          hasClamshell ? 'yes' : 'no',
          hasClamshell
            ? buildClamshellUpgradeSelectOptions(sizeOption?.clamshellPrice)[0].text
            : 'No',
        );
      }

      if (hasClamshell) {
        const clamshellStamp = optionByValue(
          stampingOptions,
          editAttrs.clamshell?.stampingId,
        ) || optionByText(stampingOptions, editAttrs.clamshell?.stamping);
        if (clamshellStamp) {
          get().updateAlbumInput(
            'clamshellDebossingType',
            clamshellStamp.value,
            clamshellStamp.text,
            { price: clamshellStamp.price },
          );
        }

        // These queries commonly finish before the clamshell fields exist.
        // Re-apply their cached options now that clamshellUpgrade created the fields.
        const {
          fabricsData,
          preDesignedCoversData,
          fontOptionsData,
        } = get();
        if (fabricsData) get().processFabricsData(fabricsData);
        if (preDesignedCoversData) get().processPreDesignedCoversData(preDesignedCoversData);
        if (fontOptionsData) get().processFontOptionsData(fontOptionsData);
      }

      get().updateAlbumTextInput(
        'coverText',
        editAttrs?.coverTextProperties?.coverTextValue
          || editAttrs?.cover_text_properties?.cover_text_value
          || '',
      );
      get().updateAlbumTextInput(
        'debossingTextTop',
        editAttrs?.upperTextProperties?.upperTextValue
          || editAttrs?.upper_text_properties?.upper_text_value
          || '',
      );
      get().updateAlbumTextInput(
        'debossingTextBottom',
        editAttrs?.lowerTextProperties?.lowerTextValue
          || editAttrs?.lower_text_properties?.lower_text_value
          || '',
      );
      if (hasClamshell) {
        get().updateAlbumTextInput(
          'clamshellCoverText',
          editAttrs.clamshell?.coverText || editAttrs.clamshell?.cover_text || '',
        );
      }
    }

    setAlbumForm((prevState) => {
      const currentSelection = prevState.inputs.debossingType?.selectedValue
        || matchedStamping?.value;
      const isSelectionValid = currentSelection
        && stampingOptions.some((option) => option.value === currentSelection);

      return {
        ...prevState,
        inputs: {
          ...prevState.inputs,
          debossingType: {
            ...(prevState.inputs.debossingType || defaultAlbumOptions.debossingType),
            options: stampingOptions,
            selectedValue: isSelectionValid ? currentSelection : undefined,
            selectedValueText: isSelectionValid
              ? (prevState.inputs.debossingType?.selectedValueText || matchedStamping?.text)
              : undefined,
            selectedValuePrice: isSelectionValid
              ? (prevState.inputs.debossingType?.selectedValuePrice ?? matchedStamping?.price)
              : undefined,
            isLoading: false,
            isDisabled: false,
          },
        },
      };
    });
  },

  processPreDesignedCoversData: (response) => {
    const {
      setPreDesignedCoversData,
      setAlbumForm,
      defaultAlbumOptions,
      albumEditLineItem,
      isAlbumEditInitializing,
    } = get();

    setPreDesignedCoversData(response);

    let coverOptions = response.data.map((cover) => ({
      value: cover.id || cover.attributes?.guid,
      text: cover.attributes?.coverType || cover.attributes?.cover_type,
    }));

    if (coverOptions.length === 0) {
      coverOptions = [{ value: 'none', text: 'None', price: 0 }];
    }

    const editAttrs = albumEditLineItem?.attributes;
    const shouldAutoMap = albumEditLineItem && isAlbumEditInitializing;
    const mappedCover = shouldAutoMap
      ? optionByValue(
        coverOptions,
        editAttrs?.preDesignedCoverOptionId ?? editAttrs?.pre_designed_cover_option_id,
      ) || optionByText(
        coverOptions,
        editAttrs?.preDesignedCover ?? editAttrs?.pre_designed_cover,
      )
      : undefined;
    const mappedClamshellCover = shouldAutoMap
      ? optionByValue(
        coverOptions,
        editAttrs?.clamshell?.preDesignedCoverOptionId
          ?? editAttrs?.clamshell?.pre_designed_cover_option_id,
      ) || optionByText(
        coverOptions,
        editAttrs?.clamshell?.preDesignedCover
          ?? editAttrs?.clamshell?.pre_designed_cover,
      )
      : undefined;

    if (shouldAutoMap && mappedCover && get().albumForm.inputs?.coverOptions) {
      get().updateAlbumInput('coverOptions', 'pre-designed', 'Pre-Designed Cover');
      get().updateAlbumInput('preDesignedCover', mappedCover.value, mappedCover.text);
    }
    if (shouldAutoMap && mappedClamshellCover
      && get().albumForm.inputs?.clamshellCoverOptions) {
      get().updateAlbumInput(
        'clamshellCoverOptions',
        'pre-designed',
        'Pre-Designed Cover',
      );
      get().updateAlbumInput(
        'clamshellPreDesignedCover',
        mappedClamshellCover.value,
        mappedClamshellCover.text,
      );
      get().updateAlbumTextInput(
        'clamshellCoverText',
        editAttrs?.clamshell?.coverText || editAttrs?.clamshell?.cover_text || '',
      );
    }

    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };
      if (newInputs.preDesignedCover) {
        newInputs.preDesignedCover = {
          ...(newInputs.preDesignedCover || defaultAlbumOptions.preDesignedCover),
          options: coverOptions,
          selectedValue: newInputs.preDesignedCover?.selectedValue || mappedCover?.value,
          selectedValueText: newInputs.preDesignedCover?.selectedValueText || mappedCover?.text,
          isLoading: false,
          isDisabled: false,
        };
      }
      if (newInputs.clamshellPreDesignedCover) {
        newInputs.clamshellPreDesignedCover = {
          ...newInputs.clamshellPreDesignedCover,
          options: coverOptions,
          selectedValue: newInputs.clamshellPreDesignedCover.selectedValue
            || mappedClamshellCover?.value,
          selectedValueText: newInputs.clamshellPreDesignedCover.selectedValueText
            || mappedClamshellCover?.text,
          isLoading: false,
        };
      }
      return {
        ...prevState,
        inputs: newInputs,
      };
    });
  },

  setAlbumPaperLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        albumPaper: { ...prevState.inputs.albumPaper, isLoading },
      },
    }));
  },

  setAlbumSizeLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        albumSize: { ...prevState.inputs.albumSize, isLoading },
      },
    }));
  },

  setFabricsLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };
      if (newInputs.coverFabric) {
        newInputs.coverFabric = { ...newInputs.coverFabric, isLoading };
      }
      return {
        ...prevState,
        inputs: newInputs,
      };
    });
  },

  setStampingsLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        debossingType: {
          ...(prevState.inputs.debossingType || {}),
          isLoading,
        },
      },
    }));
  },

  setPreDesignedCoverLoading: (isLoading) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => {

      if (!prevState.inputs.preDesignedCover) {
        return prevState;
      }
      return {
        ...prevState,
        inputs: {
          ...prevState.inputs,
          preDesignedCover: {
            ...prevState.inputs.preDesignedCover,
            isLoading,
          },
        },
      };
    });
  },

  initializeAlbumForm: (albumTypeValue, albumTypeText) => {
    const { defaultAlbumOptions, setAlbumForm, albumForm } = get();

    const albumTypeOptions = albumForm?.inputs?.albumType?.options || [];

    // Museum Case and Double Folio: coming-soon only (no paper/size dropdowns)
    if (COMING_SOON_ALBUM_TYPES.includes(albumTypeValue)) {
      const comingSoonConfig = {
        inputs: {
          albumType: {
            ...defaultAlbumOptions.albumType,
            options: albumTypeOptions,
            selectedValue: albumTypeValue,
            selectedValueText: albumTypeText,
            isLoading: false,
            isDisabled: false,
          },
        },
        meta: {
          subtotal: 0,
          requiredInputs: [],
          albumTypeId: albumTypeValue,
        },
      };
      setAlbumForm(comingSoonConfig);
      return comingSoonConfig;
    }

    const { fabricsData, albumPapersData, preDesignedCoversData } = get();
    // Get fabric options from stored data if available
    const fabricOptions = fabricsData?.data?.map((fabric) => ({
      value: fabric.id || fabric.attributes?.guid,
      text: fabric.attributes?.fabricName || fabric.attributes?.fabric_name,
    })).sort((a, b) => a.text.localeCompare(b.text)) || [];

    // Get album paper options from stored data if available
    const albumPaperOptions = albumPapersData?.data?.map((paper) => ({
      value: paper.id || paper.attributes?.guid,
      text: paper.attributes?.albumPaperName || paper.attributes?.album_paper_name,
    })) || [];

    // Get pre-designed cover options from stored data if available
    const preDesignedCoverOptions = preDesignedCoversData?.data?.map((cover) => ({
      value: cover.id || cover.attributes?.guid,
      text: cover.attributes?.coverType || cover.attributes?.cover_type,
    })) || [];
    if (preDesignedCoverOptions.length > 0 && !preDesignedCoverOptions.find(opt => opt.value === 'none')) {
      preDesignedCoverOptions.unshift({ value: 'none', text: 'None' });
    }

    const fontOptions = get().fontOptionsData?.data?.map((font) => ({
      value: font.id || font.attributes?.guid,
      text: font.attributes?.fontType || font.attributes?.font_type,
    })) || [];

    const albumTypeLower = albumTypeText?.toLowerCase() || '';

    const baseInputs = {
      albumType: {
        ...defaultAlbumOptions.albumType,
        options: albumTypeOptions,
        selectedValue: albumTypeValue,
        selectedValueText: albumTypeText,
        isLoading: false,
        isDisabled: false,
      },
      albumPaper: {
        ...defaultAlbumOptions.albumPaper,
        options: albumPaperOptions,
        isLoading: false,
        isDisabled: false,
      },
    };

    let albumFormConfig;

    if (albumTypeLower.includes('artisan album')) {
      albumFormConfig = {
        inputs: {
          ...baseInputs,
          albumSize: { ...defaultAlbumOptions.albumSize },
          coverFabric: { ...defaultAlbumOptions.coverFabric, options: fabricOptions },
          interiorFabric: { ...defaultAlbumOptions.interiorFabric, options: fabricOptions },
          numberOfSpreads: { ...defaultAlbumOptions.numberOfSpreads, isDisabled: true },
          debossingType: { ...defaultAlbumOptions.debossingType },
          coverOptions: { ...defaultAlbumOptions.coverOptions },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
            isHidden: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop, isHidden: true },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom, isHidden: true },
          clamshellUpgrade: { ...defaultAlbumOptions.clamshellUpgrade },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Album Size', 'Cover Fabric', 'Interior Fabric', 'Number of Spreads',],
          albumTypeId: albumTypeValue,
        },
      };
    } else if (albumTypeLower.includes('matted album')) {
      albumFormConfig = {
        inputs: {
          albumType: {
            ...defaultAlbumOptions.albumType,
            options: albumTypeOptions,
            selectedValue: albumTypeValue,
            selectedValueText: albumTypeText,
            isLoading: false,
            isDisabled: false,
          },
          albumPaper: {
            ...defaultAlbumOptions.albumPaper,
            label: 'Select Paper Type',
            options: albumPaperOptions,
            isLoading: false,
            isDisabled: false,
          },
          coverFabric: { ...defaultAlbumOptions.coverFabric, options: fabricOptions, isDisabled: false },
          interiorFabric: { ...defaultAlbumOptions.interiorFabric, options: fabricOptions },
          numberOfImages: { ...defaultAlbumOptions.numberOfImages, isDisabled: true },
          debossingType: { ...defaultAlbumOptions.debossingType },
          coverOptions: { ...defaultAlbumOptions.coverOptions },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
            isHidden: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop, isHidden: true },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom, isHidden: true },
          clamshellUpgrade: { ...defaultAlbumOptions.clamshellUpgrade },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Cover Fabric', 'Interior Fabric', 'Number of Images'],
          albumTypeId: albumTypeValue,
        },
      };
    } else if (albumTypeLower.includes('museum case')) {
      albumFormConfig = {
        inputs: {
          albumType: {
            ...defaultAlbumOptions.albumType,
            options: albumTypeOptions,
            selectedValue: albumTypeValue,
            selectedValueText: albumTypeText,
            isLoading: false,
            isDisabled: false,
          },
          albumPaper: {
            ...defaultAlbumOptions.albumPaper,
            label: 'Select Paper Type',
            options: albumPaperOptions,
            isLoading: false,
            isDisabled: false,
          },
          albumSize: { ...defaultAlbumOptions.albumSize, label: 'Choose Museum Case Size', isDisabled: false },
          coverFabric: { ...defaultAlbumOptions.coverFabric, label: 'Select Cover Fabric', options: fabricOptions, isDisabled: false },
          trayFabric: { ...defaultAlbumOptions.trayFabric, label: 'Select Tray Fabric', options: fabricOptions, isDisabled: false },
          numberOfMattedPrints: { ...defaultAlbumOptions.numberOfMattedPrints, isDisabled: true },
          debossingType: { ...defaultAlbumOptions.debossingType },
          coverOptions: { ...defaultAlbumOptions.coverOptions },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
            isHidden: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop, isHidden: true },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom, isHidden: true },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Museum Case Size', 'Cover Fabric', 'Tray Fabric', 'Number of Matted Prints'],
          albumTypeId: albumTypeValue,
        },
      };
    } else if (albumTypeLower.includes('double folio')) {
      albumFormConfig = {
        inputs: {
          albumType: {
            ...defaultAlbumOptions.albumType,
            options: albumTypeOptions,
            selectedValue: albumTypeValue,
            selectedValueText: albumTypeText,
            isLoading: false,
            isDisabled: false,
          },
          albumPaper: {
            ...defaultAlbumOptions.albumPaper,
            label: 'Choose Paper Type',
            options: albumPaperOptions,
            isLoading: false,
            isDisabled: false,
          },
          doubleFolioOrientation: { ...defaultAlbumOptions.doubleFolioOrientation, label: 'Double Folio Orientation', isDisabled: false },
          coverFabric: { ...defaultAlbumOptions.coverFabric, label: 'Double Folio Fabric', options: fabricOptions, isDisabled: false },
          slipcaseOption: { ...defaultAlbumOptions.slipcaseOption, isDisabled: false },
          slipcaseFabric: { ...defaultAlbumOptions.slipcaseFabric, label: 'Slipcase Fabric', options: fabricOptions, isDisabled: false },
          debossingType: { ...defaultAlbumOptions.debossingType, style: { gridColumn: 'span 1' } },
          coverOptions: { ...defaultAlbumOptions.coverOptions },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
            isHidden: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop, isHidden: true },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom, isHidden: true },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Folio Orientation', 'Folio Fabric', 'Slipcase Option'],
          albumTypeId: albumTypeValue,
        },
      };
    } else if (albumTypeLower.includes('mattedslipcase')) {
      albumFormConfig = {
        inputs: {
          albumType: {
            ...defaultAlbumOptions.albumType,
            options: albumTypeOptions,
            selectedValue: albumTypeValue,
            selectedValueText: albumTypeText,
            isLoading: false,
            isDisabled: false,
          },
          albumPaper: {
            ...defaultAlbumOptions.albumPaper,
            label: 'Select Paper Type',
            options: albumPaperOptions,
            isLoading: false,
            isDisabled: false,
          },
          coverFabric: { ...defaultAlbumOptions.coverFabric, label: 'Matted Slipcase Fabric', options: fabricOptions, isDisabled: false },
          fourFlapColorOption: { ...defaultAlbumOptions.fourFlapColorOption, isDisabled: false },
          debossingType: { ...defaultAlbumOptions.debossingType, style: { gridColumn: 'span 1' } },
          coverOptions: { ...defaultAlbumOptions.coverOptions },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
            isHidden: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop, isHidden: true },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom, isHidden: true },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Slipcase Fabric', 'Four-Flap Color'],
          albumTypeId: albumTypeValue,
        },
      };
    } else {
      albumFormConfig = {
        inputs: {
          ...baseInputs,
          albumSize: { ...defaultAlbumOptions.albumSize },
          coverFabric: { ...defaultAlbumOptions.coverFabric, options: fabricOptions },
          interiorFabric: { ...defaultAlbumOptions.interiorFabric, options: fabricOptions },
          numberOfSpreads: { ...defaultAlbumOptions.numberOfSpreads, isDisabled: true },
          debossingType: { ...defaultAlbumOptions.debossingType },
          preDesignedCover: {
            ...defaultAlbumOptions.preDesignedCover,
            options: preDesignedCoverOptions,
            isLoading: false,
            isDisabled: true,
          },
          fontOption: {
            ...defaultAlbumOptions.fontOption,
            options: fontOptions,
            isLoading: false,
            isDisabled: true,
          },
          coverText: {
            ...defaultAlbumOptions.coverText,
            isDisabled: true,
          },
          debossingTextTop: { ...defaultAlbumOptions.debossingTextTop },
          debossingTextBottom: { ...defaultAlbumOptions.debossingTextBottom },
        },
        meta: {
          subtotal: 0,
          requiredInputs: ['Paper Type', 'Album Size', 'Cover Fabric', 'Interior Fabric'],
          albumTypeId: albumTypeValue,
        },
      };
    }

    setAlbumForm(albumFormConfig);

    return albumFormConfig;
  },

  // Reset album form to initial state
  resetAlbumForm: () => {
    const { getAlbumForm, setAlbumForm } = get();
    setAlbumForm(getAlbumForm());
  },

  // Update album input value
  updateAlbumInput: (key, value, valueText, additionalData = {}) => {
    const { setAlbumForm, albumSizesData, defaultAlbumOptions } = get();
    setAlbumForm((prevState) => {
      const newInputs = { ...prevState.inputs };
      const newMeta = { ...prevState.meta };

      // Define the field hierarchy for cascading resets
      const fieldHierarchy = [
        'albumType',
        'albumPaper',
        'albumSize',
        'coverFabric',
        'interiorFabric',
        'trayFabric',
        // 'numberOfSpreads',
        'numberOfImages',
        'numberOfMattedPrints',
        'fourFlapColorOption',
        'debossingType',
        'coverOptions',
        'preDesignedCover',
        'fontOption',
        'coverText',
        'debossingTextTop',
        'debossingTextBottom',
        'clamshellUpgrade',
        'clamshellOutsideFabric',
        'clamshellTrayFabric',
        'clamshellDebossingType',
        'clamshellCoverOptions',
        'clamshellPreDesignedCover',
        'clamshellFontOption',
        'clamshellCoverText',
        'slipcaseOption',
        'slipcaseFabric',
        'doubleFolioOrientation',
      ];

      // Independent field groups - these should NOT reset each other
      const stampingFields = ['debossingType', 'debossingTextTop', 'debossingTextBottom', 'clamshellUpgrade'];
      const preDesignedCoverFields = ['preDesignedCover'];
      const fontCoverFields = ['fontOption', 'coverText', 'debossingTextTop', 'debossingTextBottom'];
      const attributeFields = ['albumPaper', 'albumSize', 'coverFabric', 'interiorFabric', 'trayFabric', 'numberOfImages', 'numberOfMattedPrints', 'fourFlapColorOption'];
      const clamshellFields = ['clamshellOutsideFabric', 'clamshellTrayFabric', 'clamshellDebossingType', 'clamshellCoverOptions', 'clamshellPreDesignedCover', 'clamshellFontOption', 'clamshellCoverText'];
      const mainCoverFields = ['coverOptions', 'preDesignedCover', 'fontOption', 'coverText', 'debossingTextTop', 'debossingTextBottom'];

      // Helper to reset a field to its default state
      const resetField = (fieldKey) => {
        if (!newInputs[fieldKey]) return;

        const defaultField = defaultAlbumOptions[fieldKey];
        if (defaultField) {
          newInputs[fieldKey] = {
            ...newInputs[fieldKey],
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            isDisabled: true,
          };
        }
        if (fieldKey === 'fontOption') newMeta.fontOptionId = undefined;
        if (fieldKey === 'preDesignedCover') newMeta.preDesignedCoverId = undefined;
        if (fieldKey === 'clamshellFontOption') newMeta.clamshellFontOptionId = undefined;
        if (fieldKey === 'clamshellPreDesignedCover') newMeta.clamshellPreDesignedCoverId = undefined;
      };

      const shouldSkipReset = (changedField, fieldToReset) => {
        if (get().isAlbumEditInitializing) {
          return true;
        }
        if (mainCoverFields.includes(changedField) && stampingFields.includes(fieldToReset)) {
          return true;
        }
        if (stampingFields.includes(changedField) && mainCoverFields.includes(fieldToReset)) {
          return true;
        }
        if (changedField === 'clamshellUpgrade' && clamshellFields.includes(fieldToReset)) {
          return true;
        }
        if (clamshellFields.includes(changedField) && mainCoverFields.includes(fieldToReset)) {
          return true;
        }
        if (mainCoverFields.includes(changedField) && clamshellFields.includes(fieldToReset)) {
          return true;
        }
        return false;
      };

      // Reset all fields below the changed field in the hierarchy
      const currentIndex = fieldHierarchy.indexOf(key);
      if (currentIndex !== -1) {
        for (let i = currentIndex + 1; i < fieldHierarchy.length; i++) {
          const fieldToReset = fieldHierarchy[i];
          if (shouldSkipReset(key, fieldToReset)) {
            continue;
          }
          resetField(fieldToReset);
        }
        
        // Also reset corresponding meta values for downstream fields
        if (key === 'albumPaper' || currentIndex < fieldHierarchy.indexOf('albumPaper')) {
          newMeta.albumSizeId = undefined;
          newMeta.basePrice = undefined;
          newMeta.perSpreadPrice = undefined;
        }
        if (key === 'coverFabric' || currentIndex < fieldHierarchy.indexOf('coverFabric')) {
          newMeta.outerFabricId = undefined;
        }
        if (key === 'interiorFabric' || key === 'trayFabric' || currentIndex < fieldHierarchy.indexOf('interiorFabric')) {
          newMeta.innerFabricId = undefined;
        }
        if (key === 'debossingType' || currentIndex < fieldHierarchy.indexOf('debossingType')) {
          newMeta.stampingId = undefined;
          newMeta.stampingPrice = undefined;
        }
      }

      const clearsAlbumSelection =
        value === 'default' || value === '' || value === undefined;

      const nextFieldState = {
        ...newInputs[key],
        selectedValue: value,
        selectedValueText: valueText,
      };
      if (additionalData.price !== undefined) {
        nextFieldState.selectedValuePrice = additionalData.price;
      } else if (clearsAlbumSelection) {
        nextFieldState.selectedValuePrice = undefined;
      }
      newInputs[key] = nextFieldState;

      // Update meta IDs based on what was selected
      if (key === 'albumType') {
        newMeta.albumTypeId = value;
      } else if (key === 'albumPaper') {
        newMeta.albumPaperId = value;
      } else if (key === 'albumSize') {
        newMeta.albumSizeId = value && value !== 'default' ? value : undefined;
        if (value && value !== 'default') {
          if (additionalData.price !== undefined) {
            newMeta.basePrice = additionalData.price;
          }
          if (additionalData.perSpreadPrice !== undefined) {
            newMeta.perSpreadPrice = additionalData.perSpreadPrice;
          }
        } else {
          newMeta.basePrice = undefined;
          newMeta.perSpreadPrice = undefined;
        }
      } else if (key === 'coverFabric') {
        newMeta.outerFabricId = value;
      } else if (key === 'interiorFabric' || key === 'trayFabric') {
        newMeta.innerFabricId = value;
      } else if (key === 'debossingType') {
        if (value && value !== 'default') {
          newMeta.stampingId = value;
        } else {
          newMeta.stampingId = undefined;
        }
        if (additionalData.price !== undefined) {
          newMeta.stampingPrice = additionalData.price;
        } else if (!value || value === 'default') {
          newMeta.stampingPrice = undefined;
        }
      }

      // Enable dependent fields based on selection
      if (key === 'albumPaper' && value && value !== 'default') {
        if (newInputs.albumSize) {
          // Check if album sizes data is already cached
          const hasAlbumSizesData = albumSizesData?.data?.length > 0;
          const albumSizeOptions = hasAlbumSizesData
            ? albumSizesData.data.map(albumSizeApiRowToSelectOption)
            : [];

          newInputs.albumSize = {
            ...newInputs.albumSize,
            options: hasAlbumSizesData ? albumSizeOptions : newInputs.albumSize.options,
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            isLoading: !hasAlbumSizesData,
            isDisabled: false,
          };
        }

        if (newInputs.numberOfSpreads) {
          const paperTypeText = (valueText || '').toLowerCase();
          const maxSpreads = getMaxSpreadsForPaperType(paperTypeText);
          const spreadOptions = generateSpreadOptions(maxSpreads);

          const currentSpread = parseInt(newInputs.numberOfSpreads.selectedValue, 10);
          const isCurrentStillValid =
          currentSpread && currentSpread >= 10 && currentSpread <= maxSpreads;

          newInputs.numberOfSpreads = {
            ...newInputs.numberOfSpreads,
            options: spreadOptions,
            isDisabled: false,
            selectedValue: isCurrentStillValid ? newInputs.numberOfSpreads.selectedValue : undefined,
            selectedValueText: isCurrentStillValid ? newInputs.numberOfSpreads.selectedValueText : undefined,
          };
        }
      }

      if (key === 'albumPaper' && newInputs.clamshellUpgrade) {
        newInputs.clamshellUpgrade = {
          ...newInputs.clamshellUpgrade,
          options: buildClamshellUpgradeSelectOptions(null),
        };
      }

      if (key === 'albumSize' && value && value !== 'default') {
        if (newInputs.coverFabric) {
          newInputs.coverFabric = {
            ...newInputs.coverFabric,
            isDisabled: false,
          };
        }
        if (newInputs.doubleFolioOrientation) {
          newInputs.doubleFolioOrientation = {
            ...newInputs.doubleFolioOrientation,
            isDisabled: false,
          };
        }
      }

      if (key === 'albumSize' && newInputs.clamshellUpgrade) {
        let shellParsed = null;
        if (value && value !== 'default') {
          const selectedSizeOpt = newInputs.albumSize?.options?.find(
            (o) => String(o.value) === String(value),
          );
          const cp = selectedSizeOpt?.clamshellPrice;
          if (cp != null && Number.isFinite(cp) && cp > 0) {
            shellParsed = cp;
          }
        }
        newInputs.clamshellUpgrade = {
          ...newInputs.clamshellUpgrade,
          options: buildClamshellUpgradeSelectOptions(shellParsed),
        };
      }

      if (key === 'coverFabric' && value && value !== 'default') {
        if (newInputs.interiorFabric) {
          newInputs.interiorFabric = {
            ...newInputs.interiorFabric,
            isDisabled: false,
          };
        }
        if (newInputs.trayFabric) {
          newInputs.trayFabric = {
            ...newInputs.trayFabric,
            isDisabled: false,
          };
        }
        if (newInputs.fourFlapColorOption) {
          newInputs.fourFlapColorOption = {
            ...newInputs.fourFlapColorOption,
            isDisabled: false,
          };
        }
        if (newInputs.slipcaseFabric) {
          newInputs.slipcaseFabric = {
            ...newInputs.slipcaseFabric,
            isDisabled: false,
          };
        }
      }

      if ((key === 'interiorFabric' || key === 'trayFabric') && value && value !== 'default') {
        if (newInputs.numberOfSpreads) {
          newInputs.numberOfSpreads = {
            ...newInputs.numberOfSpreads,
            isDisabled: false,
          };
        }
        if (newInputs.numberOfImages) {
          newInputs.numberOfImages = {
            ...newInputs.numberOfImages,
            isDisabled: false,
          };
        }
        if (newInputs.numberOfMattedPrints) {
          newInputs.numberOfMattedPrints = {
            ...newInputs.numberOfMattedPrints,
            isDisabled: false,
          };
        }
      }

      if ((key === 'numberOfSpreads' || key === 'numberOfImages' || key === 'numberOfMattedPrints' ||
        key === 'fourFlapColorOption') && value && value !== 'default') {
        if (newInputs.debossingType) {
          newInputs.debossingType = {
            ...newInputs.debossingType,
            isDisabled: false,
          };
        }
      }

      if (key === 'debossingType') {
        if (value && value !== 'default' && value !== 'none') {
          if (newInputs.coverOptions) {
            newInputs.coverOptions = {
              ...newInputs.coverOptions,
              isDisabled: false,
              isHidden: false,
            };
          }
        } else {
          // If None, hide cover options and all sub-fields
          if (newInputs.coverOptions) {
            newInputs.coverOptions = {
              ...newInputs.coverOptions,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
          if (newInputs.preDesignedCover) {
            newInputs.preDesignedCover = {
              ...newInputs.preDesignedCover,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
          if (newInputs.fontOption) {
            newInputs.fontOption = {
              ...newInputs.fontOption,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
          if (newInputs.coverText) {
            newInputs.coverText = {
              ...newInputs.coverText,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
          if (newInputs.debossingTextTop) {
            newInputs.debossingTextTop = {
              ...newInputs.debossingTextTop,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
          if (newInputs.debossingTextBottom) {
            newInputs.debossingTextBottom = {
              ...newInputs.debossingTextBottom,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
        }

        // Enable clamshell upgrade regardless of debossing selection
        if (newInputs.clamshellUpgrade) {
          newInputs.clamshellUpgrade = {
            ...newInputs.clamshellUpgrade,
            isDisabled: false,
          };
        }
      }

      if (key === 'coverOptions') {
        if (value === 'pre-designed') {
          if (newInputs.preDesignedCover) {
            newInputs.preDesignedCover = {
              ...newInputs.preDesignedCover,
              isHidden: false,
              isDisabled: false
            };
          }
          if (newInputs.coverText) {
            newInputs.coverText = {
              ...newInputs.coverText,
              isHidden: false,
              isDisabled: false,
              label: 'Text for Cover'
            };
          }
          if (newInputs.fontOption) {
            newInputs.fontOption = {
              ...newInputs.fontOption,
              isHidden: true,
              isDisabled: true, selectedValue: undefined
            };
          }
          if (newInputs.debossingTextTop) {
            newInputs.debossingTextTop = {
              ...newInputs.debossingTextTop,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
          if (newInputs.debossingTextBottom) {
            newInputs.debossingTextBottom = {
              ...newInputs.debossingTextBottom,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
        } else if (value === 'text-only') {
          if (newInputs.fontOption) {
            newInputs.fontOption = {
              ...newInputs.fontOption,
              isHidden: false,
              isDisabled: false
            };
          }
          if (newInputs.debossingTextTop) {
            newInputs.debossingTextTop = {
              ...newInputs.debossingTextTop,
              isHidden: false,
              isDisabled: false
            };
          }
          if (newInputs.debossingTextBottom) {
            newInputs.debossingTextBottom = {
              ...newInputs.debossingTextBottom,
              isHidden: false,
              isDisabled: false
            };
          }
          if (newInputs.preDesignedCover) {
            newInputs.preDesignedCover = {
              ...newInputs.preDesignedCover,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
          if (newInputs.coverText) {
            newInputs.coverText = {
              ...newInputs.coverText,
              isHidden: true,
              isDisabled: true,
              selectedValue: ''
            };
          }
        }
      }

      if (key === 'preDesignedCover') {
        if (value && value !== 'default') {
          if (newInputs.coverText) {
            newInputs.coverText = {
              ...newInputs.coverText,
              isDisabled: false
            };
          }
          if (newInputs.clamshellUpgrade) {
            newInputs.clamshellUpgrade = { 
              ...newInputs.clamshellUpgrade, 
              isDisabled: false 
            };
          }
        }
      }

      if (key === 'fontOption') {
        if (value && value !== 'default') {
          if (newInputs.debossingTextTop) {
            newInputs.debossingTextTop = {
              ...newInputs.debossingTextTop, isDisabled: false
            };
          }
          if (newInputs.debossingTextBottom) {
            newInputs.debossingTextBottom = {
              ...newInputs.debossingTextBottom, isDisabled: false
            };
          }
          if (newInputs.clamshellUpgrade) {
            newInputs.clamshellUpgrade = {
              ...newInputs.clamshellUpgrade, isDisabled: false
            };
          }
        }
      }

      if (key === 'clamshellDebossingType') {
        if (value && value !== 'default' && value !== 'none') {
          if (newInputs.clamshellCoverOptions) {
            newInputs.clamshellCoverOptions = {
              ...newInputs.clamshellCoverOptions,
              isDisabled: false,
              isHidden: false,
            };
          }
        } else {
          // none selected — hide cover options and all sub-fields
          if (newInputs.clamshellCoverOptions) {
            newInputs.clamshellCoverOptions = {
              ...newInputs.clamshellCoverOptions,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined,
            };
          }
          if (newInputs.clamshellPreDesignedCover) {
            newInputs.clamshellPreDesignedCover = {
              ...newInputs.clamshellPreDesignedCover,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined,
            };
          }
          if (newInputs.clamshellFontOption) {
            newInputs.clamshellFontOption = {
              ...newInputs.clamshellFontOption,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined,
            };
          }
          if (newInputs.clamshellCoverText) {
            newInputs.clamshellCoverText = {
              ...newInputs.clamshellCoverText,
              isHidden: true,
              isDisabled: true,
              selectedValue: '',
            };
          }
        }
      }
      
      if (key === 'clamshellCoverOptions') {
        if (value === 'pre-designed') {
          if (newInputs.clamshellPreDesignedCover) {
            newInputs.clamshellPreDesignedCover = {
              ...newInputs.clamshellPreDesignedCover,
              isHidden: false,
              isDisabled: false
            };
          }
          if (newInputs.clamshellCoverText) {
            newInputs.clamshellCoverText = {
              ...newInputs.clamshellCoverText,
              isHidden: false,
              isDisabled: true,
              selectedValue: get().isAlbumEditInitializing
                ? newInputs.clamshellCoverText.selectedValue
                : '',
            };
          }
          if (newInputs.clamshellFontOption) {
            newInputs.clamshellFontOption = {
              ...newInputs.clamshellFontOption,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
        } else if (value === 'text-only') {
          if (newInputs.clamshellFontOption) {
            newInputs.clamshellFontOption = {
              ...newInputs.clamshellFontOption,
              isHidden: false,
              isDisabled: false
            };
          }

          if (newInputs.clamshellCoverText) {
            newInputs.clamshellCoverText = {
              ...newInputs.clamshellCoverText,
              isHidden: false,
              isDisabled: true,
              selectedValue: get().isAlbumEditInitializing
                ? newInputs.clamshellCoverText.selectedValue
                : '',
            };
          }
          if (newInputs.clamshellPreDesignedCover) {
            newInputs.clamshellPreDesignedCover = {
              ...newInputs.clamshellPreDesignedCover,
              isHidden: true,
              isDisabled: true,
              selectedValue: undefined
            };
          }
        }
      }

      if (key === 'clamshellPreDesignedCover') {
        if (value && value !== 'default') {
          if (newInputs.clamshellCoverText) {
            newInputs.clamshellCoverText = {
              ...newInputs.clamshellCoverText,
              isDisabled: false
            };
          }
        }
      }

      if (key === 'clamshellFontOption') {
        if (value && value !== 'default') {
          if (newInputs.clamshellCoverText) {
            newInputs.clamshellCoverText = {
              ...newInputs.clamshellCoverText,
              isDisabled: false
            };
          }
        }
      }

      // Handle clamshellUpgrade selection
      if (key === 'clamshellUpgrade') {
        const { fabricsData, stampingsData, preDesignedCoversData, fontOptionsData } = get();

        if (value === 'yes') {
          const fabricOptions = fabricsData?.data?.map((fabric) => ({
            value: fabric.id || fabric.attributes?.guid,
            text: fabric.attributes?.fabricName || fabric.attributes?.fabric_name || '',
          })).sort((a, b) => a.text.localeCompare(b.text)) || [];

          const clamshellTrayFabricOptions = fabricOptions.filter(
            (opt) => !fabricTextIsClamshellTrayCoverFabricName(opt.text),
          );

          let stampingOptions =
            stampingsData?.data?.map((stamping) => {
              const stampingType = stamping.attributes?.stampingType || '';
              const price = parseFloat(stamping.attributes?.price) || 0;
              const displayText = `${stampingType} ($${price.toFixed(2)})`;
              return {
                value: stamping.id || stamping.attributes?.guid,
                text: displayText,
                price,
              };
            }) ?? [];

          stampingOptions.unshift({ value: 'none', text: 'None', price: 0, stampingType: 'None' });


          let preDesignedCoverOptions = [];
          if (preDesignedCoversData?.data && Array.isArray(preDesignedCoversData.data)) {
            preDesignedCoverOptions = preDesignedCoversData.data.map((cover) => ({
              value: cover.id || cover.attributes?.guid,
              text: cover.attributes?.coverType || cover.attributes?.cover_type || '',
            }));
          }

          if (preDesignedCoverOptions.length === 0) {
            preDesignedCoverOptions = [{ value: 'none', text: 'None' }];
          }

          let fontOptions = [];
          if (fontOptionsData?.data && Array.isArray(fontOptionsData.data)) {
            fontOptions = fontOptionsData.data.map((font) => ({
              value: font.id || font.attributes?.guid,
              text: font.attributes?.fontType || font.attributes?.font_type || '',
            }));
          }

          // Add clamshell-specific fields to the form
          newInputs.clamshellOutsideFabric = {
            ...defaultAlbumOptions.clamshellOutsideFabric,
            options: fabricOptions,
            selectedValue: newInputs.clamshellOutsideFabric?.selectedValue,
            selectedValueText: newInputs.clamshellOutsideFabric?.selectedValueText,
            isLoading: false,
            isDisabled: false,
          };

          newInputs.clamshellTrayFabric = {
            ...defaultAlbumOptions.clamshellTrayFabric,
            options: clamshellTrayFabricOptions,
            selectedValue: newInputs.clamshellTrayFabric?.selectedValue,
            selectedValueText: newInputs.clamshellTrayFabric?.selectedValueText,
            isLoading: false,
            isDisabled: !newInputs.clamshellOutsideFabric?.selectedValue,
          };

          newInputs.clamshellDebossingType = {
            ...defaultAlbumOptions.clamshellDebossingType,
            options: stampingOptions,
            selectedValue: newInputs.clamshellDebossingType?.selectedValue,
            selectedValueText: newInputs.clamshellDebossingType?.selectedValueText,
            isLoading: false,
            isDisabled: !newInputs.clamshellTrayFabric?.selectedValue,
          };

          newInputs.clamshellCoverOptions = {
            ...defaultAlbumOptions.clamshellCoverOptions,
            isLoading: false,
            isDisabled: !newInputs.clamshellDebossingType?.selectedValue || newInputs.clamshellDebossingType?.selectedValue === 'none',
            isHidden: !newInputs.clamshellDebossingType?.selectedValue || newInputs.clamshellDebossingType?.selectedValue === 'none',
          };

          newInputs.clamshellPreDesignedCover = {
            ...defaultAlbumOptions.clamshellPreDesignedCover,
            options: preDesignedCoverOptions,
            selectedValue: newInputs.clamshellPreDesignedCover?.selectedValue,
            selectedValueText: newInputs.clamshellPreDesignedCover?.selectedValueText,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          };

          newInputs.clamshellFontOption = {
            ...defaultAlbumOptions.clamshellFontOption,
            options: fontOptions,
            selectedValue: newInputs.clamshellFontOption?.selectedValue,
            selectedValueText: newInputs.clamshellFontOption?.selectedValueText,
            isLoading: false,
            isDisabled: true,
            isHidden: true,
          };

          newInputs.clamshellCoverText = {
            ...defaultAlbumOptions.clamshellCoverText,
            selectedValue: newInputs.clamshellCoverText?.selectedValue
              || (get().isAlbumEditInitializing
                ? get().albumEditLineItem?.attributes?.clamshell?.coverText
                  || get().albumEditLineItem?.attributes?.clamshell?.cover_text
                : '')
              || '',
            isDisabled: true,
            isHidden: true,
          };

          const mergedReq = [...(newMeta.requiredInputs || [])];
          [newInputs.clamshellOutsideFabric?.requiredInput, newInputs.clamshellTrayFabric?.requiredInput]
            .filter(Boolean)
            .forEach((reqKey) => {
              if (!mergedReq.includes(reqKey)) mergedReq.push(reqKey);
            });
          newMeta.requiredInputs = mergedReq;
        } else if (value === 'no' || !value || value === 'default') {
          // Remove clamshell-specific fields when 'no' is selected
          delete newInputs.clamshellOutsideFabric;
          delete newInputs.clamshellTrayFabric;
          delete newInputs.clamshellDebossingType;
          delete newInputs.clamshellPreDesignedCover;
          delete newInputs.clamshellCoverOptions;   
          delete newInputs.clamshellFontOption;
          delete newInputs.clamshellCoverText;

          // Clear clamshell meta values
          newMeta.clamshellOutsideFabricId = undefined;
          newMeta.clamshellTrayFabricId = undefined;
          newMeta.clamshellStampingId = undefined;
          newMeta.clamshellPreDesignedCoverId = undefined;
          newMeta.clamshellFontOptionId = undefined;

          const outsideReqKey = defaultAlbumOptions.clamshellOutsideFabric?.requiredInput;
          const trayReqKey = defaultAlbumOptions.clamshellTrayFabric?.requiredInput;
          newMeta.requiredInputs = (newMeta.requiredInputs || []).filter(
            (item) => item !== outsideReqKey && item !== trayReqKey,
          );
        }
      }

      // Handle clamshell field cascading
      if (key === 'clamshellOutsideFabric' && value && value !== 'default') {
        newMeta.clamshellOutsideFabricId = value;
        if (newInputs.clamshellTrayFabric) {
          newInputs.clamshellTrayFabric = {
            ...newInputs.clamshellTrayFabric,
            isDisabled: false,
          };
        }
      }

      if (key === 'clamshellTrayFabric' && value && value !== 'default') {
        newMeta.clamshellTrayFabricId = value;
        if (newInputs.clamshellDebossingType) {
          newInputs.clamshellDebossingType = {
            ...newInputs.clamshellDebossingType,
            isDisabled: false,
          };
        }
      }

      if (key === 'clamshellDebossingType') {
        if (value && value !== 'default') {
          newMeta.clamshellStampingId = value;
          if (newInputs.clamshellPreDesignedCover) {
            newInputs.clamshellPreDesignedCover = {
              ...newInputs.clamshellPreDesignedCover,
              isDisabled: false,
            };
          }
        } else {
          newMeta.clamshellStampingId = undefined;
        }
      }

      if (key === 'clamshellPreDesignedCover' && value && value !== 'default') {
        newMeta.clamshellPreDesignedCoverId = value;
        if (newInputs.clamshellFontOption) {
          newInputs.clamshellFontOption = {
            ...newInputs.clamshellFontOption,
            isDisabled: false,
          };
        }
      }

      if (key === 'clamshellFontOption' && value && value !== 'default') {
        newMeta.clamshellFontOptionId = value;
        if (newInputs.clamshellCoverText) {
          newInputs.clamshellCoverText = {
            ...newInputs.clamshellCoverText,
            isDisabled: false,
          };
        }
      }

      // Handle fontOption selection to enable coverText
      if (key === 'fontOption' && value && value !== 'default') {
        newMeta.fontOptionId = value;
        if (newInputs.coverText) {
          newInputs.coverText = {
            ...newInputs.coverText,
            isDisabled: false,
          };
        }
      }

      // Handle preDesignedCover selection
      if (key === 'preDesignedCover' && value && value !== 'default') {
        newMeta.preDesignedCoverId = value;
      }

      return {
        meta: newMeta,
        inputs: newInputs,
      };
    });
  },

  // Update album text input value
  updateAlbumTextInput: (key, value) => {
    const { setAlbumForm } = get();
    setAlbumForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        [key]: {
          ...prevState.inputs[key],
          selectedValue: value,
        },
      },
    }));
  },

  // Calculate album subtotal
  calculateAlbumSubtotal: () => {
    const { albumForm } = get();
    if (!albumForm || !albumForm.meta) return '0.00';

    let subtotal = 0;

    // Base price from album size
    const albumSizeSel = albumForm.inputs?.albumSize?.selectedValue;
    const albumSizePrice =
      albumSizeSel && albumSizeSel !== 'default'
        ? parseFloat(albumForm.inputs?.albumSize?.selectedValuePrice) || 0
        : 0;
    subtotal += albumSizePrice;

    // Additional spread price
    const spreadsValue = albumForm.inputs?.numberOfSpreads?.selectedValue;
    const numberOfSpreads = parseInt(spreadsValue, 10) || 10;
    const baseSpreads = albumForm.meta.baseSpreads || 10;
    const perSpreadPrice = parseFloat(albumForm.meta.perSpreadPrice) || 0;

    if (numberOfSpreads > baseSpreads) {
      subtotal += (numberOfSpreads - baseSpreads) * perSpreadPrice;
    }

    // Stamping price (ignore stale price when "Choose One" is selected)
    const debossingSel = albumForm.inputs?.debossingType?.selectedValue;
    const stampingPrice =
      debossingSel && debossingSel !== 'default'
        ? parseFloat(albumForm.inputs?.debossingType?.selectedValuePrice) || 0
        : 0;
    subtotal += stampingPrice;

    // Clamshell upgrade (from selected album size) + clamshell cover stamping
    if (albumForm.inputs?.clamshellUpgrade?.selectedValue === 'yes') {
      const sizeVal = albumForm.inputs?.albumSize?.selectedValue;
      const sizeOpts = albumForm.inputs?.albumSize?.options;
      const selectedSizeOpt = sizeOpts?.find((o) => String(o.value) === String(sizeVal));
      const clamshellBase = parseFloat(selectedSizeOpt?.clamshellPrice);
      if (Number.isFinite(clamshellBase) && clamshellBase > 0) {
        subtotal += clamshellBase;
      }

      const clDebossSel = albumForm.inputs?.clamshellDebossingType?.selectedValue;
      const clamshellStampingPrice =
        clDebossSel && clDebossSel !== 'default'
          ? parseFloat(albumForm.inputs?.clamshellDebossingType?.selectedValuePrice) || 0
          : 0;
      subtotal += clamshellStampingPrice;
    }

    return subtotal.toFixed(2);
  },

  /** Missing labels for required non-text selects (honours `input.requiredInput` when set). */
  getAlbumMissingRequiredSelectionLabels: () => {
    const { albumForm, getAlbumInputKeys } = get();
    if (!albumForm?.inputs) return [];

    const requiredInputs = albumForm.meta?.requiredInputs || [];
    const missing = [];

    getAlbumInputKeys().forEach((inputKey) => {
      const input = albumForm.inputs[inputKey];
      if (!input || input.inputType === 'text') return;
      if (!albumFieldIsRequired(input, requiredInputs)) return;
      if (!input.selectedValue || input.selectedValue === 'default') {
        const label = input.label;
        if (label && !missing.includes(label)) missing.push(label);
      }
    });

    return missing;
  },

  // Check if album form is valid (all required inputs filled)
  isAlbumFormValid: () => get().getAlbumMissingRequiredSelectionLabels().length === 0,

  getAlbumInputKeys: () => [
    'albumType',
    'albumPaper',
    'albumSize',
    'coverFabric',
    'interiorFabric',
    'numberOfSpreads',
    'debossingType',
    'coverOptions',
    'preDesignedCover',
    'fontOption',
    'coverText',
    'debossingTextTop',
    'debossingTextBottom',
    'clamshellUpgrade',
    'clamshellOutsideFabric',
    'clamshellTrayFabric',
    'clamshellDebossingType',
    'clamshellCoverOptions',
    'clamshellPreDesignedCover',
    'clamshellFontOption',
    'clamshellCoverText',
    'numberOfImages',
    'numberOfMattedPrints',
    'trayFabric',
    'doubleFolioOrientation',
    'slipcaseOption',
    'slipcaseFabric',
    'fourFlapColorOption',
  ],
}));

export default useAlbumSelectionStore;
